import React, { useCallback } from 'react';
import { useTranslation } from '../../localization/useTranslation';
import { Size } from '../Common/Icons';
import classNames from 'classnames';
import {
  NavItem,
  Split,
  SplitItem, Tooltip,
  Truncate
} from '@patternfly/react-core';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTabletOrMobile } from '../../utils/hooks/useTabletOrMobile';

import styles from './SideBar.module.css';

export const SideBarItemType = Object.freeze({
  PATH: 'path', // default if type is null
  LINK: 'link',
  HEADING: 'heading'
});

export const SideNavLink = React.memo(({
  icon: Icon,
  isSideBarCollapsed,
  to,
  isActive,
  target,
  href,
  name,
  toggleSideBar,
  isTabletOrMobile
}) => {
  const T = useTranslation();
  const LinkComponent = to ? NavLink : (props) => <a {..._.omit(props, 'children')} >{ props.children }</a>;
  const _toggleSideBar = (isTabletOrMobile && !isSideBarCollapsed) ? toggleSideBar : _.noop;


  const MenuItem = useCallback(() => {
    return (
      <NavItem
        onClick={_toggleSideBar}
        isActive={isActive}
        to={to}
        itemId={to}
      >
        <LinkComponent
          style={isSideBarCollapsed ? { justifyContent: 'center' } : {}}
          to={to}
          href={href}
          target={target}
        >
          <Split hasGutter>
            <SplitItem className={styles.sidebarIcon}>
              <Icon size={isTabletOrMobile ? Size.XL : Size.LARGE}/>
            </SplitItem>
            { !isSideBarCollapsed && <SplitItem isFilled>
              <Truncate
                tooltipPosition={'right'}
                className={classNames(styles.sidebarFont,
                  isTabletOrMobile && styles.sidebarTabletFontsize,
                  !isTabletOrMobile && styles.sidebarDesktopFontsize,

                )}
                content={T.localize(name)} />
            </SplitItem>
            }
          </Split>
        </LinkComponent>
      </NavItem>
    );
  }, [T, _toggleSideBar, href, isActive, isSideBarCollapsed, isTabletOrMobile, name, target, to]);

  return (
    (isSideBarCollapsed) ?
      <Tooltip content={T.localize(name)} position={'right'}>
        <MenuItem />
      </Tooltip>
      : <MenuItem />
  );
});

export const MenuItemPropType = PropTypes.shape({
  type: PropTypes.oneOf(_.values(SideBarItemType)),
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  href: PropTypes.string,
  target: PropTypes.string,
  name: PropTypes.string,
  isEnabled: PropTypes.func,
  visibleWhenLoggedOut: PropTypes.bool
});

const SideBarItemTypeProps = {
  menuItem: MenuItemPropType,
  isSideBarCollapsed: PropTypes.bool,

  // For some reason eslint thinks these prop types are unused, but they are used. Suppress these warnings
  // eslint-disable-next-line react/no-unused-prop-types
  toggleSideBar: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  isTabletOrMobile: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  isActive: PropTypes.bool
};

const SideBarPath = React.memo(({ menuItem, isSideBarCollapsed, isActive, toggleSideBar, isTabletOrMobile }) => {
  const menuPath = _.isArray(menuItem.path) ? _.head(menuItem.path) : menuItem.path;
  const to = _.isObject(menuPath) ? menuPath.path : menuPath;

  return (<SideNavLink
    icon={menuItem.icon}
    to={to}
    name={menuItem.name}
    isSideBarCollapsed={isSideBarCollapsed}
    isActive={isActive}
    toggleSideBar={toggleSideBar}
    isTabletOrMobile={isTabletOrMobile}
  />);
});
SideBarPath.propTypes = SideBarItemTypeProps;

const SideBarLink = React.memo(({ menuItem, isSideBarCollapsed, toggleSideBar, isTabletOrMobile }) => {
  return (<SideNavLink
    icon={menuItem.icon}
    href={menuItem.href}
    target={menuItem.target}
    name={menuItem.name}
    isSideBarCollapsed={isSideBarCollapsed}
    toggleSideBar={toggleSideBar}
    isTabletOrMobile={isTabletOrMobile}
  />);
});
SideBarLink.propTypes = SideBarItemTypeProps;

const SideBarItemTypes = {
  [SideBarItemType.PATH]: SideBarPath,
  [SideBarItemType.LINK]: SideBarLink,
  // Default values
  null: SideBarPath,
  undefined: SideBarPath
};

export const SideBarItem = React.memo(({ menuItem, isActive, isSideBarCollapsed, toggleSideBar }) => {
  const isTabletOrMobile = useTabletOrMobile();
  const Component = SideBarItemTypes[menuItem.type];
  return (<Component
    menuItem={menuItem}
    isActive={isActive}
    isSideBarCollapsed={isSideBarCollapsed}
    toggleSideBar={toggleSideBar}
    isTabletOrMobile={isTabletOrMobile}
  />);
});

SideBarItem.propTypes = {
  menuItem: MenuItemPropType,
  isActive: PropTypes.bool.isRequired,
  isSideBarCollapsed: PropTypes.bool.isRequired,
  toggleSideBar: PropTypes.func.isRequired
};
